if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          sliderCounterWrapper: this.querySelector('[data-sc-current-counter-wrapper]'),
          sliderCounterCurrent: this.querySelectorAll('[data-sc-current-slide]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.enableDesktopDrag();

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      enableDesktopDrag() {
        if (!this.elements.viewer.slider || !this.dataset.desktopLayout.includes('thumbnail')) return;

        const slider = this.elements.viewer.slider;
        let isPointerDown = false;
        let hasDragged = false;
        let startX = 0;
        let startScrollLeft = 0;
        let activePointerId = null;
        let dragFrame = null;
        let targetScrollLeft = 0;
        let previousX = 0;
        let previousTime = 0;
        let pointerVelocity = 0;

        slider.addEventListener('dragstart', (event) => event.preventDefault());

        slider.addEventListener('pointerdown', (event) => {
          if (!this.mql.matches || event.pointerType === 'touch' || event.button !== 0) return;

          if (this.desktopDragAnimationFrame) cancelAnimationFrame(this.desktopDragAnimationFrame);
          this.desktopDragAnimationFrame = null;
          slider.classList.remove('is-snapping');
          isPointerDown = true;
          hasDragged = false;
          activePointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = slider.scrollLeft;
          targetScrollLeft = startScrollLeft;
          previousX = startX;
          previousTime = performance.now();
          pointerVelocity = 0;
        });

        slider.addEventListener('pointermove', (event) => {
          if (!isPointerDown || event.pointerId !== activePointerId) return;

          const dragDistance = event.clientX - startX;
          if (Math.abs(dragDistance) < 8 && !hasDragged) return;

          const currentTime = performance.now();
          pointerVelocity = (event.clientX - previousX) / Math.max(currentTime - previousTime, 1);
          previousX = event.clientX;
          previousTime = currentTime;

          if (!hasDragged) {
            hasDragged = true;
            slider.classList.add('is-dragging');
            slider.setPointerCapture(activePointerId);
          }

          targetScrollLeft = this.getRubberScrollPosition(startScrollLeft - dragDistance * 1.08);

          if (!dragFrame) {
            const animateDrag = () => {
              slider.scrollLeft += (targetScrollLeft - slider.scrollLeft) * 0.32;
              dragFrame = isPointerDown && hasDragged ? requestAnimationFrame(animateDrag) : null;
            };

            dragFrame = requestAnimationFrame(animateDrag);
          }

          event.preventDefault();
        });

        const endDrag = (event) => {
          if (!isPointerDown || event.pointerId !== activePointerId) return;

          const endX = event.clientX || previousX;
          isPointerDown = false;
          activePointerId = null;
          slider.classList.remove('is-dragging');

          if (hasDragged) {
            if (dragFrame) cancelAnimationFrame(dragFrame);
            dragFrame = null;
            this.snapDraggedSlider(endX - startX, pointerVelocity, startScrollLeft);
            this.preventNextMediaClick = true;
            window.setTimeout(() => {
              this.preventNextMediaClick = false;
            }, 300);
          }
        };

        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);
        slider.addEventListener(
          'click',
          (event) => {
            if (!this.preventNextMediaClick) return;

            event.preventDefault();
            event.stopImmediatePropagation();
            this.preventNextMediaClick = false;
          },
          true
        );
      }

      getRubberScrollPosition(position) {
        const slider = this.elements.viewer.slider;
        const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

        if (position < 0) return position * 0.35;
        if (position > maxScrollLeft) return maxScrollLeft + (position - maxScrollLeft) * 0.35;

        return position;
      }

      snapDraggedSlider(dragDistance, pointerVelocity, startScrollLeft) {
        const visibleMedia = Array.from(this.elements.viewer.sliderItemsToShow || []);
        if (!visibleMedia.length) return;

        const slider = this.elements.viewer.slider;
        const slideWidth = visibleMedia[1]
          ? visibleMedia[1].offsetLeft - visibleMedia[0].offsetLeft
          : visibleMedia[0].clientWidth;
        const threshold = Math.min(Math.max(slideWidth * 0.18, 60), 140);
        const velocityThreshold = 0.45;
        const startIndex = this.getClosestMediaIndex(visibleMedia, startScrollLeft);
        let targetIndex = startIndex;

        if (dragDistance < -threshold || pointerVelocity < -velocityThreshold) {
          targetIndex = startIndex + 1;
        } else if (dragDistance > threshold || pointerVelocity > velocityThreshold) {
          targetIndex = startIndex - 1;
        } else {
          targetIndex = this.getClosestMediaIndex(visibleMedia, slider.scrollLeft);
        }

        targetIndex = Math.min(Math.max(targetIndex, 0), visibleMedia.length - 1);

        this.animateSliderTo(visibleMedia[targetIndex].offsetLeft);
      }

      animateSliderTo(targetScrollLeft) {
        const slider = this.elements.viewer.slider;
        const startScrollLeft = slider.scrollLeft;
        const distance = targetScrollLeft - startScrollLeft;
        const duration = 520;
        const startTime = performance.now();

        if (this.desktopDragAnimationFrame) cancelAnimationFrame(this.desktopDragAnimationFrame);
        slider.classList.add('is-snapping');

        const animate = (currentTime) => {
          const progress = Math.min((currentTime - startTime) / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          slider.scrollLeft = startScrollLeft + distance * easedProgress;

          if (progress < 1) {
            this.desktopDragAnimationFrame = requestAnimationFrame(animate);
          } else {
            this.desktopDragAnimationFrame = null;
            slider.classList.remove('is-snapping');
          }
        };

        this.desktopDragAnimationFrame = requestAnimationFrame(animate);
      }

      getClosestMediaIndex(visibleMedia, scrollLeft) {
        return visibleMedia.reduce((closestIndex, media, index) => {
          return Math.abs(media.offsetLeft - scrollLeft) < Math.abs(visibleMedia[closestIndex].offsetLeft - scrollLeft)
            ? index
            : closestIndex;
        }, 0);
      }

      onSlideChanged(event) {
        this.setActiveMediaClass(event.detail.currentElement);
        this.updateSliderCounterWrapper(event.detail.currentElement);
        this.updateSliderCounter(event.detail.currentPage);
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMediaClass(activeMedia) {
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
      }

      updateSliderCounterWrapper(activeMedia) {
        if (!this.elements.sliderCounterWrapper || !activeMedia) return;

        const mediaContainer = activeMedia.querySelector('.product-media-container');
        if (!mediaContainer) return;

        this.elements.sliderCounterWrapper.style.setProperty(
          '--ratio',
          mediaContainer.style.getPropertyValue('--ratio') || '1'
        );
        this.elements.sliderCounterWrapper.style.setProperty(
          '--preview-ratio',
          mediaContainer.style.getPropertyValue('--preview-ratio') || '1'
        );
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);
        this.updateSliderCounterWrapper(activeMedia);
        this.updateSliderCounter(this.getMediaPosition(activeMedia));

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`);
        this.updateSliderCounterWrapper(activeMedia);
        this.updateSliderCounter(this.getMediaPosition(activeMedia));
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      getMediaPosition(media) {
        if (!media) return null;
        const visibleMedia = Array.from(this.elements.viewer.sliderItemsToShow || []);
        const visibleIndex = visibleMedia.indexOf(media);
        return visibleIndex >= 0 ? visibleIndex + 1 : media.dataset.mediaPosition;
      }

      updateSliderCounter(position) {
        if (!this.elements.sliderCounterCurrent.length || !position) return;
        this.elements.sliderCounterCurrent.forEach((counter) => {
          counter.textContent = position;
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
